import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { MdArrowBack } from 'react-icons/md'
import AppTable from '../../shared/AppTable'
import AppButton from '../../shared/AppButton'
import Pagination from '../../components/Pagination'
import TableSkeletonLoader from '../../common/TableLoading'
import claims from '../../gql/claims'
import toast from 'react-hot-toast'

function SummaryCard ({ label, value }) {
  return (
    <div className='rounded-md border border-slate-200 bg-slate-50 px-3 py-2'>
      <p className='text-[10px] font-medium uppercase tracking-wide text-slate-500'>
        {label}
      </p>
      <p className='mt-1 text-base font-semibold text-slate-800'>
        {value ?? 0}
      </p>
    </div>
  )
}

export default function ClaimDiagnosisBulkJobDetails () {
  const { i18n } = useTranslation()
  const isRTL = i18n.dir() === 'rtl'
  const navigate = useNavigate()
  const { jobId } = useParams()
  const [page, setPage] = useState(1)
  const limit = 10

  const { data, loading } = useQuery(
    claims.GET_CLAIM_DIAGNOSIS_BULK_LOG_DETAILS,
    {
      variables: { jobId, page, limit },
      fetchPolicy: 'network-only',
      onError: error => toast.error(error?.graphQLErrors?.[0]?.message || error?.message || 'Something went wrong.')
    }
  )

  const details = data?.getClaimDiagnosisBulkLogDetails
  const job = details?.job || {}
  const rows = details?.data || []
  const pages = details?.pages || 1

  const columns = [
    { header: 'Transaction ID', field: 'transaction_id' },
    { header: 'Patient', field: 'patient_name' },
    {
      header: 'Old ICD / Description',
      render: row => (
        <div>
          <p className='font-medium'>{row.old_icd_code_id}</p>
          <p
            className='mt-0.5 max-w-[260px] truncate text-[11px] text-slate-500'
            title={row.old_description}
          >
            {row.old_description || '—'}
          </p>
        </div>
      )
    },
    {
      header: 'New ICD / Description',
      render: row => (
        <div>
          <p className='font-medium'>{row.new_icd_code_id}</p>
          <p
            className='mt-0.5 max-w-[260px] truncate text-[11px] text-slate-500'
            title={row.new_description}
          >
            {row.new_description || '—'}
          </p>
        </div>
      )
    },
    {
      header: 'Sequence',
      field: 'sequence_no',
      align: 'center'
    },
    { header: 'Reverted', render: row => (row.reverted ? 'Yes' : 'No') }
  ]

  return (
    <div className='custom-table-scrollbar max-h-[calc(100vh-120px)] w-full overflow-y-auto p-3'>
      <div className='mb-2 flex items-center gap-2'>
        <div
          onClick={() => navigate('/claims/diagnosis-bulk-replace')}
          className='bg-[#0070C2] hover:bg-[#0070C2]/80 cursor-pointer rounded-full p-1'
          title='Back to diagnosis replacement logs'
        >
          <MdArrowBack
            className={
              isRTL
                ? 'text-white h-4 w-4 rotate-180'
                : 'text-white h-4 w-4'
            }
          />
        </div>
        <div>
          <h1 className='text-lg font-semibold text-black/70'>
            Job Details
          </h1>
          <p className='text-[11px] text-slate-500'>
            Job ID: <span className='font-mono'>{jobId}</span>
          </p>
        </div>
      </div>

      <div className='mb-2 grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-5'>
        <SummaryCard label='Status' value={job.status || '—'} />
        <SummaryCard label='Created By' value={job.created_by_name || '—'} />
        <SummaryCard label='Date Range' value={job.from_date && job.to_date ? `${job.from_date} — ${job.to_date}` : '—'} />
        <SummaryCard label='Batch Size' value={job.batch_size || '—'} />
        <SummaryCard label='Mappings' value={job.replacement_count || 0} />
        <SummaryCard label='Claims Scanned' value={job.total_claim_requests_scanned || 0} />
        <SummaryCard label='Claims Updated' value={job.total_claims_updated || 0} />
        <SummaryCard label='Diagnoses Matched' value={job.total_diagnoses_matched || 0} />
        <SummaryCard label='Diagnoses Updated' value={job.total_diagnoses_updated || 0} />
        <SummaryCard label='Skipped' value={job.skipped_count || 0} />
      </div>

      <AppTable
        columns={columns}
        data={rows}
        loading={loading}
        actions={false}
        bodyRowPadding='7px'
        LoaderComponent={
          <TableSkeletonLoader columnCount={6} rowCount={8} />
        }
      />

      <div className='mt-2'>
        <Pagination
          currentPage={page}
          totalPages={pages}
          onPageChange={newPage => setPage(newPage)}
        />
      </div>
    </div>
  )
}
